import { useTranslation } from 'react-i18next';
import { getExpiryDate } from '../../utils/getPaymentSourceCardInfo/getExpiryDate';
import type { BookingPaymentSource } from '../../types/api/v2/obt/model/booking-payment-source';
import { PaymentSourceType } from '../../types/api/v2/obt/model/payment-source-type';
import { CreditCardAccessType } from '../../types/api/v2/obt/model/credit-card-access-type';
import {
  getFullAssetPath,
  getCarVendorLogo,
  getPaymentCardLogo,
  maskCardNumber,
  isPaymentAddOnViolation,
  ADDON_PAYMENT_VIOLATION_TEXT,
  CARD_EXPIRED_TEXT,
  sentenceCase,
} from '../../utils';
import type { Card } from '../../types';
import { CardCompany } from '../../types';
import { defineCommonMessage } from '../../translations/defineMessage';
import { isBefore, getTodayDateTime } from '../../date-utils';
import { CardOwnershipLabelEnum } from '../../types/api/v2/obt/model/card';

const UATP_CARD_LOGO = getFullAssetPath('/assets/images/card-companies/uatp', 'png');

/**
 * @deprecated Use getPaymentSourceCardInfo instead
 */
export const usePaymentSourceCardInfo = (paymentSource: BookingPaymentSource | undefined) => {
  const { t: tt } = useTranslation('COMMON');

  let cardNumber = '';
  let cardTitle = '';
  let cardLogo = '';
  let cardCompany = '';
  let cardExpireDate = '';
  let maskedCardNumber = '';
  let customLogo = '';
  let isCentralCard = false;
  let budgetBalance = '';
  let isAirlineRewards = false;
  let cardType = '';

  if (
    (paymentSource?.type === PaymentSourceType.Card ||
      paymentSource?.type === PaymentSourceType.VendorProgramPayment) &&
    paymentSource?.metadata &&
    ('cardMetadata' in paymentSource?.metadata ||
      ('vendorProgramPaymentDescriptor' in paymentSource?.metadata &&
        paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata &&
        'airlineProgram' in paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata))
  ) {
    if (
      'vendorProgramPaymentDescriptor' in paymentSource?.metadata &&
      paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata &&
      'airlineProgram' in paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata
    ) {
      const airlineProgramMetadata =
        paymentSource.metadata.vendorProgramPaymentDescriptor.vendorProgramPaymentMetadata.airlineProgram
          ?.airlineProgramMetadata;
      let uappCard: Card | undefined;
      let uatpCard: Card | undefined;
      if (airlineProgramMetadata && 'uaPassPlusMetadata' in airlineProgramMetadata) {
        uappCard = airlineProgramMetadata.uaPassPlusMetadata?.uatpInfo?.card;
      }
      if (airlineProgramMetadata && 'uatpMetadata' in airlineProgramMetadata) {
        uatpCard = airlineProgramMetadata.uatpMetadata?.card;
      }

      cardType = `${sentenceCase(tt('{{ownershipLabel}} card', { ownershipLabel: CardOwnershipLabelEnum.Central }))}`;

      if (uappCard) {
        cardTitle = uappCard.label || uappCard.name || '';
        cardCompany = uappCard.company?.toString() ?? '';
        cardLogo = getPaymentCardLogo(cardCompany);
        maskedCardNumber = maskCardNumber(uappCard.number ?? '**** **** **** ****', { split: false });
        cardExpireDate = getExpiryDate(uappCard);
        isAirlineRewards = true;
      } else if (uatpCard) {
        cardTitle = uatpCard.label || uatpCard.name || '';
        cardCompany = uatpCard.company?.toString() ?? '';
        cardLogo = UATP_CARD_LOGO;
        maskedCardNumber = maskCardNumber(uatpCard.number ?? '**** **** **** ****', { split: false });
        cardExpireDate = getExpiryDate(uatpCard);
        isAirlineRewards = true;
      }
    } else if ('cardMetadata' in paymentSource.metadata) {
      const metadata = paymentSource.metadata.cardMetadata;
      const accessType = paymentSource?.paymentAccess?.creditCardAccess?.accessType;

      const { number, label, name, company = CardCompany.None, ownershipLabel } = metadata.card;
      cardNumber = number || '';
      cardTitle = label || name || '';
      cardLogo = getPaymentCardLogo(company);
      cardCompany = company;
      cardExpireDate = getExpiryDate(metadata.card);
      maskedCardNumber = maskCardNumber(cardNumber);
      cardType = ownershipLabel ? `${sentenceCase(tt('{{ownershipLabel}} card', { ownershipLabel }))}` : '';

      if (company === CardCompany.AirTravelUatp) {
        cardLogo = paymentSource.isRequired ? getPaymentCardLogo(company) : UATP_CARD_LOGO;
      } else {
        cardLogo = getPaymentCardLogo(company);
      }
      if (accessType === CreditCardAccessType.Centralised) {
        isCentralCard = true;
      }
    }
  }

  if (
    paymentSource?.type === PaymentSourceType.VirtualCard &&
    paymentSource?.metadata &&
    'virtualCardMetadata' in paymentSource?.metadata
  ) {
    const metadata = paymentSource.metadata.virtualCardMetadata;
    const accessType = paymentSource?.paymentAccess?.creditCardAccess?.accessType;

    const { cardLabel, company } = metadata;
    cardNumber = 'XXXX-XXXX-XXXX-XXXX';
    cardTitle = cardLabel ?? defineCommonMessage('Virtual card').message;
    cardCompany = company.toString();
    cardLogo = getPaymentCardLogo(cardCompany);
    maskedCardNumber = maskCardNumber(cardNumber);
    cardExpireDate = `XX/XX`;
    if (accessType === CreditCardAccessType.Centralised) {
      isCentralCard = true;
    }
    cardType = `${sentenceCase(tt('{{ownershipLabel}} card', { ownershipLabel: CardOwnershipLabelEnum.Central }))}`;
  }

  if (
    paymentSource?.type === PaymentSourceType.CustomPaymentMethod &&
    paymentSource?.metadata &&
    'customPaymentMethodMetadata' in paymentSource?.metadata &&
    paymentSource?.metadata?.customPaymentMethodMetadata &&
    'brexBudgetMetadata' in paymentSource?.metadata?.customPaymentMethodMetadata
  ) {
    const metadata = paymentSource.metadata?.customPaymentMethodMetadata?.brexBudgetMetadata;
    cardTitle = metadata?.budgetName ?? '';
    cardCompany = 'MASTERCARD';
    cardLogo = getPaymentCardLogo('MASTERCARD');
    // TODO: fix when backend sends this data
    maskedCardNumber = maskCardNumber('**** **** **** ****');

    customLogo = getPaymentCardLogo('brex');
    budgetBalance = metadata?.budgetRemainingBalanceFormatted ?? '';
  }

  if (
    paymentSource?.type === PaymentSourceType.VendorProgramPayment &&
    paymentSource?.metadata &&
    'vendorProgramPaymentDescriptor' in paymentSource?.metadata &&
    paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata &&
    'directBilling' in paymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata
  ) {
    const metadata = paymentSource.metadata.vendorProgramPaymentDescriptor;
    cardCompany = metadata?.vendorInfo?.vendorName ?? '';
    cardLogo = getCarVendorLogo(metadata?.vendorInfo?.vendorId || '');
    if (metadata?.vendorProgramPaymentMetadata && 'directBilling' in metadata?.vendorProgramPaymentMetadata) {
      cardTitle = metadata.vendorProgramPaymentMetadata.directBilling?.label ?? cardCompany;
      maskedCardNumber = maskCardNumber(
        metadata?.vendorProgramPaymentMetadata.directBilling?.directBillingCode ?? '**** **** **** ****',
        { split: false },
      );
    }
  }

  const isAddOnPolicyViolation = isPaymentAddOnViolation(paymentSource);

  const isCardExpired = isBefore(cardExpireDate, getTodayDateTime('MM/YY'), 'MM/YY');

  const getDisabledCardInfoText = () => {
    /**
     * Expiry takes precedence because an expired card cannot be used for anything
     */
    switch (true) {
      case isCardExpired:
        return CARD_EXPIRED_TEXT.message;
      case isAddOnPolicyViolation:
        return ADDON_PAYMENT_VIOLATION_TEXT.message;
      default:
        return '';
    }
  };

  return {
    id: paymentSource?.id,
    paymentSourceType: paymentSource?.type,
    isUsable: paymentSource?.isUsable && !isAddOnPolicyViolation,
    accessType: paymentSource?.paymentAccess?.creditCardAccess?.accessType,
    isAirlineRewards,
    cardNumber,
    cardTitle,
    cardLogo,
    cardCompany,
    cardExpireDate,
    maskedCardNumber,
    isCentralCard,
    customLogo,
    budgetBalance,
    isAddOnPolicyViolation,
    isCardExpired,
    disabledCardInfoText: getDisabledCardInfoText(),
    cardType,
  };
};
